import { Link, useMatch, useResolvedPath } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import toast from "react-hot-toast";
import "./NavBar.css";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut(auth);
      setUser(null);
      toast.success(`Success! You've been logged out.`);
      navigate("/");
    } catch (error) {
      toast.error(`Logout failed: ${error.message}`);
    } finally {
      setLoggingOut(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <nav className="nav">
        <div className="nav-content">
          <Link to="/" className="logo">
            <picture>
              <source srcSet="/header.avif" type="image/avif" />
              <source srcSet="/header.webp" type="image/webp" />
              <img src="/header.avif" alt="hawks eats logo" />
            </picture>
          </Link>

          <button
            ref={toggleRef}
            className="menu-toggle"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {isOpen ? (
              <picture>
                <source srcSet="/x.avif" type="image/avif" />
                <source srcSet="/x.webp" type="image/webp" />
                <img src="/x.avif" alt="Close menu" />
              </picture>
            ) : (
              <picture>
                <source srcSet="/menu.avif" type="image/avif" />
                <source srcSet="/menu.webp" type="image/webp" />
                <img src="/menu.avif" alt="Open menu" />
              </picture>
            )}
          </button>
          <ul className={`nav-list`}>
            <CustomLink to="/add-place">+ Place</CustomLink>
            <CustomLink to="/explore">Explore</CustomLink>
            {user && <CustomLink to="/favorites">Favorites</CustomLink>}
            {user ? (
              <li>
                <button className="logout-btn" onClick={handleLogout}>
                  Log Out
                </button>
              </li>
            ) : (
              <CustomLink to="/login">Login</CustomLink>
            )}
          </ul>
        </div>
      </nav>

      {isOpen && (
        <nav ref={menuRef} className="mobile-nav">
          <ul className={`mobile-nav-list`}>
            <CustomLink to="/add-place">+ Place</CustomLink>
            <CustomLink to="/explore">Explore</CustomLink>
            {user && <CustomLink to="/favorites">Favorites</CustomLink>}
            {user ? (
              <li>
                <button
                  className="logout-btn"
                  onClick={handleLogout}
                  disabled={loggingOut}
                >
                  {loggingOut ? "Logging out..." : "Log Out"}
                </button>
              </li>
            ) : (
              <CustomLink to="/login">Login</CustomLink>
            )}
          </ul>
        </nav>
      )}
    </>
  );
}

function CustomLink({ to, children, ...props }) {
  const resolvedPath = useResolvedPath(to);
  const isActive = useMatch({ path: resolvedPath.pathname, end: true });
  return (
    <li className={isActive ? "active" : ""}>
      <Link to={to} {...props}>
        {children}
      </Link>
    </li>
  );
}
